import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";

const POLL_INTERVAL_MS = 2500;
const MAX_POLLS = 40; // ~100s

// Post-checkout page: PhonePe redirects the browser here with ?txn_id=...
// We poll /finalize until we get a definitive status, then show it.
export default function PaymentResult() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const txnId = params.get("txn_id");
  const kind = params.get("kind") || "";
  const [status, setStatus] = useState("polling");
  const [txn, setTxn] = useState(null);
  const [message, setMessage] = useState("Waiting for PhonePe to confirm your payment…");
  const pollsRef = useRef(0);
  const timerRef = useRef(null);
  const returnTo = (() => {
    try { return sessionStorage.getItem(`pp_return_${txnId}`) || defaultReturn(kind); }
    catch (_) { return defaultReturn(kind); }
  })();

  useEffect(() => {
    if (!txnId) { setStatus("error"); setMessage("Missing transaction id."); return; }
    const tick = async () => {
      pollsRef.current += 1;
      try {
        const { data } = await api.post(`/payments/phonepe/finalize/${txnId}`);
        setTxn(data.transaction);
        if (data.payment_status === "paid") {
          setStatus("success");
          setMessage("Payment successful.");
          try { sessionStorage.removeItem(`pp_return_${txnId}`); } catch (_) {}
          toast.success("Payment successful");
          return;
        }
        if (data.payment_status === "failed" || data.state === "FAILED") {
          setStatus("failed");
          setMessage("Payment failed or was cancelled.");
          return;
        }
      } catch (e) {
        // On error, keep polling for a bit — could be a transient upstream error
        setMessage("Confirming with PhonePe…");
        if (pollsRef.current > 5) {
          setStatus("error");
          setMessage(apiError(e));
          return;
        }
      }
      if (pollsRef.current >= MAX_POLLS) {
        setStatus("pending");
        setMessage("Still waiting for PhonePe. Your payment will reconcile once we receive confirmation.");
        return;
      }
      timerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
    };
    tick();
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [txnId]);

  const iconFor = () => {
    if (status === "success") return <CheckCircle2 size={72} className="text-green-400" />;
    if (status === "failed" || status === "error") return <XCircle size={72} className="text-red-400" />;
    return <Loader2 size={72} className="text-brand-orange animate-spin" />;
  };
  const titleFor = () => {
    if (status === "success") return "PAYMENT SUCCESSFUL";
    if (status === "failed") return "PAYMENT FAILED";
    if (status === "error") return "COULD NOT CONFIRM";
    if (status === "pending") return "STILL WAITING";
    return "CONFIRMING PAYMENT";
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-16" data-testid="payment-result">
      <div className="max-w-lg w-full bg-[#131A2B] border border-white/12 p-10 text-center">
        <div className="flex justify-center mb-6">{iconFor()}</div>
        <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-2">// Payment · PhonePe UPI</div>
        <h1 className="font-display text-3xl uppercase font-black tracking-tighter leading-none mb-4" data-testid="pr-title">
          {titleFor()}.
        </h1>
        <p className="text-white/70 text-sm leading-relaxed mb-8" data-testid="pr-message">{message}</p>

        {txn && (
          <div className="bg-black/30 border border-white/10 p-4 mb-6 text-left text-xs font-mono">
            <Row k="Amount" v={`₹${Number(txn.amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`} />
            <Row k="For"      v={txn.description || txn.kind} />
            <Row k="Ref"      v={txn.phonepe_merchant_order_id || txn.razorpay_order_id || "—"} />
            {txn.paid_at && <Row k="Paid at" v={new Date(txn.paid_at).toLocaleString()} />}
          </div>
        )}

        <button
          onClick={() => navigate(returnTo)}
          data-testid="pr-continue"
          className="w-full bg-brand-orange text-black px-6 py-3 font-bold uppercase tracking-widest text-sm hover:glow-orange inline-flex items-center justify-center gap-2"
        >
          Continue <ArrowRight size={16} />
        </button>
        {status === "pending" && (
          <button
            onClick={() => window.location.reload()}
            className="w-full mt-3 border border-white/20 text-white px-6 py-3 font-mono uppercase tracking-widest text-xs hover:bg-white/6"
            data-testid="pr-retry-poll"
          >
            Check again
          </button>
        )}
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-3 py-1 border-b border-white/5 last:border-b-0">
      <span className="text-white/40 uppercase tracking-widest">{k}</span>
      <span className="text-white/90 text-right break-all">{v}</span>
    </div>
  );
}

function defaultReturn(kind) {
  switch (kind) {
    case "invoice":         return "/parent";
    case "tournament_team": return "/tournaments";
    case "donation":        return "/donate";
    case "wallet_topup":    return "/parent";
    case "membership":      return "/member";
    default:                return "/";
  }
}
